import React, { Component, Fragment } from 'react';
import get from 'lodash/get';
import axios from 'axios';
import { Alert } from '@lightspeed/flame/Alert';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Spinner } from '@lightspeed/flame/Spinner';
import Text, { TextLink } from '@lightspeed/cirrus-text';

import Header from './Header';
import EcomProvisioningForm from './Form';
import {
  formatForAPI,
  formatInitialValuesFromAPI,
  formatErrorsForFormik,
  createInitialErrors,
} from './utils';

import { paths as provisioningPaths } from '../routes';

const merchantos = window.merchantos;

export default class EcomProvisioningFormRoot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      initialData: {},
      initialValues: {
        shopId: '',
        shopName: '',
        shopEmail: '',
        languageCode: '',
        publish: 'all',
        countryName: '',
        countryCode: '',
        regionName: '',
        regionCode: '',
        address1: '',
        address2: '',
        city: '',
        zipcode: '',
        signInEmail: '',
      },
      isFetching: true,
    };

    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            icon: 'ecom',
            text: 'Get Started with Omnichannel',
          },
        ],
      },
    });

    this.submitFormData = this.submitFormData.bind(this);
  }

  componentDidMount() {
    const { history } = this.props;
    // @TODO Move this to use Retail API with Redux setup
    // Fetch data from Admin API: GET /admin/ecom/provisioning
    return axios
      .get('/admin/ecom/provisioning')
      .then(response => {
        const { data } = response.data;
        const { ecomProvisioning } = data;

        // Send user to confirmation page if already provisioning
        if (ecomProvisioning.status) {
          history.replace('/ecom/provisioning/confirmation');
          return;
        }

        this.setState(
          prevState => ({
            initialData: data,
            initialValues: {
              ...prevState.initialValues,
              ...formatInitialValuesFromAPI(data),
            },
            isFetching: false,
          }),
          () => {
            // @TODO: Move the Formik form into it's own component and only render it once we
            // have the data. This way we don't have to save the validateForm
            // method on runtime and on every render.
            if (this.validateForm) {
              this.validateForm();
            }
          }
        );
      })
      .catch(e => {
        const error = 'An error occured, please contact support';
        const formErrors = get(e, 'response.data.errors');

        this.setState({
          isFetching: false,
          error,
          // eslint-disable-next-line react/no-unused-state
          formErrors,
        });
      });
  }

  submitFormData(values, actions) {
    const { history } = this.props;

    return axios
      .post('/admin/ecom/provisioning', formatForAPI(values))
      .then(() => {
        actions.setSubmitting(false);
        this.setState({ error: '' });
        history.replace(provisioningPaths.confirmation);
      })
      .catch(e => {
        const error = 'An error occured, please contact support';
        const formErrors = get(e, 'response.data.errors');

        actions.setSubmitting(false);
        actions.setErrors(formatErrorsForFormik(formErrors));

        this.setState({
          error: formErrors && formErrors.length ? '' : error,
        });
      });
  }

  render() {
    const { initialValues, initialData, isFetching, error } = this.state;
    const initialErrors = isFetching ? {} : createInitialErrors(initialValues);

    const getEcomLanguageOptions = () =>
      initialData.ecomLanguages.map(language => ({
        value: language.id,
        label: language.name,
      }));

    const getShopListingsOptions = () =>
      initialData.shopListings.map(shop => ({
        value: shop.id,
        label: shop.name,
      }));

    const getPublishOptions = () => {
      const { ecomProvisioning } = initialData;
      const { productsCount, productsLimit } = ecomProvisioning;
      const isProductsOverLimit = productsCount > productsLimit;

      return [
        {
          value: 'all',
          label: isProductsOverLimit
            ? `Publish the first ${productsLimit} items.`
            : 'Publish all items to eCom',
        },
        { value: 'none', label: 'Decide later which items to publish' },
      ];
    };

    return (
      <div className="cr-p-4 cr-sans-serif cirrus">
        {!!error && (
          <div className="cr-mb-2">
            <Alert type="danger" noCloseBtn>
              <p className="cr-m-0">{error}</p>
            </Alert>
          </div>
        )}

        <div className="cr-max-width-content cr-display-block-centered">
          {isFetching ? (
            <Fragment>
              <Header isDisabled />
              <Card large>
                <CardSection>
                  Gathering data... <Spinner size="1rem" color="gray-300" />
                </CardSection>
              </Card>
            </Fragment>
          ) : (
            <EcomProvisioningForm
              staticData={initialData}
              initialValues={initialValues}
              initialErrors={initialErrors}
              onSubmit={this.submitFormData}
              ecomLanguagesOptions={getEcomLanguageOptions()}
              shopListingsOptions={getShopListingsOptions()}
              publishOptions={getPublishOptions()}
            />
          )}

          <Text body className="cr-pt-2">
            Need help getting started? Follow our{' '}
            <TextLink
              href="https://retail-support.lightspeedhq.com/hc/en-us/articles/115000314354-Retail-with-eCom-Guided-Setup-Introduction"
              target="_blank"
            >
              Retail with eCom - Guided Setup
            </TextLink>
          </Text>
        </div>
      </div>
    );
  }
}
